import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Typography, TextField, Button,
  Alert, IconButton, InputAdornment, Chip, Link, CircularProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNotification } from '../context/NotificationContext';
import { useAnthropicKey } from '../context/AnthropicKeyContext';
import { saveKey as apiSaveKey, deleteKey as apiDeleteKey } from '../services/api';

/**
 * BYOK (Bring Your Own Key) settings page — V1-10.
 *
 * The user's Anthropic API key is stored encrypted on the backend (table
 * user_anthropic_keys). This page only manages connect/replace/disconnect.
 * The plaintext key never lives in localStorage; only the last_4 metadata
 * comes back from the server for display.
 */

export default function Settings() {
  const { t } = useTranslation();
  const { success, warning, error: notifyError } = useNotification();
  const { has_key: hasKey, last_4, refresh } = useAnthropicKey();

  const [inputKey, setInputKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Display mask using only the last 4 chars (we don't store more on the
  // client). Format: '••••••••XYZ9'
  const maskedKey = last_4 ? `${'•'.repeat(8)}${last_4}` : '';

  const validateKeyFormat = (key) => {
    const trimmed = key.trim();
    if (!trimmed) return t('settings.key_required');
    if (!trimmed.startsWith('sk-ant-')) return t('settings.key_invalid');
    if (trimmed.length < 40) return t('settings.key_too_short');
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formatError = validateKeyFormat(inputKey);
    if (formatError) {
      setError(formatError);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const res = await apiSaveKey(inputKey.trim());
      if (!res.data?.ok) {
        // Anthropic rejected the key — surface their error inline. We don't
        // toast this because the form is the right place for the user to fix.
        setError(res.data?.error || t('settings.validation_failed'));
        setSaving(false);
        return;
      }
      // Successful save → wipe the form, refresh global key state so other
      // pages flip out of "missing key" mode immediately.
      setInputKey('');
      setShowKey(false);
      await refresh();
      success(t('settings.save_success'));
    } catch (e2) {
      notifyError(e2.friendlyMessage || t('settings.save_failed'));
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('settings.remove_confirm'))) return;
    setRemoving(true);
    try {
      await apiDeleteKey();
      await refresh();
      warning(t('settings.removed_warning'));
    } catch (e) {
      notifyError(e.friendlyMessage || t('settings.remove_failed'));
    }
    setRemoving(false);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {t('settings.page_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('settings.intro')}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <KeyIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {t('settings.key_label')}
          </Typography>
          {hasKey ? (
            <Chip
              label={t('settings.chip_active')}
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
              sx={{ ml: 'auto' }}
            />
          ) : (
            <Chip
              label={t('settings.chip_not_set')}
              size="small"
              color="warning"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        {hasKey && (
          <Box
            p={1.5} mb={2}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {t('settings.current_key_label')}
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {maskedKey}
            </Typography>
          </Box>
        )}

        <form onSubmit={handleSave}>
          <TextField
            label={hasKey ? t('settings.replace_field_label') : t('settings.enter_field_label')}
            type={showKey ? 'text' : 'password'}
            fullWidth
            value={inputKey}
            onChange={(e) => {
              setInputKey(e.target.value);
              if (error) setError('');
            }}
            placeholder="sk-ant-..."
            autoComplete="off"
            error={Boolean(error)}
            helperText={error || ' '}
            disabled={saving || removing}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowKey(s => !s)}
                    edge="end"
                    size="small"
                    aria-label={showKey ? t('settings.hide_key_aria') : t('settings.show_key_aria')}
                  >
                    {showKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              type="submit"
              variant="contained"
              disabled={!inputKey.trim() || saving || removing}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {saving
                ? t('settings.saving')
                : (hasKey ? t('settings.update_button') : t('settings.save_button'))}
            </Button>
            {hasKey && (
              <Button
                color="error"
                variant="outlined"
                startIcon={removing ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleRemove}
                disabled={saving || removing}
              >
                {removing ? t('settings.removing') : t('settings.remove_button')}
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          {t('settings.no_key_yet_title')}
        </Typography>
        <Typography variant="body2">
          {t('settings.create_key_intro')}{' '}
          <Link
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
          >
            console.anthropic.com/settings/keys
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
          {t('settings.create_key_outro')}
        </Typography>
      </Alert>
    </Container>
  );
}
